import { Box, type SxProps, type Theme } from "@mui/material";
import { useId, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import {
  SITE_COLOR,
  SITE_MEDIA,
  SITE_VAR,
  SITE_WRAP,
  siteFluidFont,
} from "src/domains/shared/constants/styles/site";
import { SiteImage } from "./media/SiteImage";
import { revealRef } from "./motion/reveal";
import { SectionHead } from "./SectionHead";
import { SiteSection } from "./SiteSection";
import { SiteTitle } from "./SiteText";

/** 그리기 전 자리 높이(넓은 화면, 900px 이하) */
const EDUCATION_OFFSCREEN_SIZE = [800, 1000] as const;

const EDUCATION_STYLES = {
  /** 왼쪽 대표 사진이 오른쪽 네 항목 높이만큼 선다. 900px 이하는 사진 위·항목 아래 */
  layout: {
    display: "grid",
    gridTemplateColumns: "minmax(0,11fr) minmax(0,9fr)",
    gap: "clamp(32px,5vw,72px)",
    alignItems: "stretch",
    [SITE_MEDIA.tablet]: { gridTemplateColumns: "minmax(0,1fr)", gap: "36px" },
  },
  photo: {
    position: "relative",
    margin: 0,
    minHeight: "440px",
    borderRadius: SITE_VAR.radiusImage,
    overflow: "hidden",
    background: SITE_COLOR.panel,
    "& img": {
      position: "absolute",
      inset: 0,
      width: "100%",
      height: "100%",
      objectFit: "cover",
      // 손가락이 짚는 도면과 조끼 글자가 늘 보이도록 가운데 오른쪽을 본다
      objectPosition: "62% 50%",
    },
    [SITE_MEDIA.tablet]: { minHeight: 0, aspectRatio: "3/2" },
  },
  list: {
    listStyle: "none",
    margin: 0,
    padding: 0,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  item: {
    display: "grid",
    gridTemplateColumns: "92px minmax(0,1fr)",
    columnGap: "16px",
    padding: "22px 0",
    borderTop: `1px solid ${SITE_COLOR.line2}`,
    "&:first-of-type": { borderTop: 0, paddingTop: 0 },
    "&:last-of-type": { paddingBottom: 0 },
    [SITE_MEDIA.phone]: {
      gridTemplateColumns: "minmax(0,1fr)",
      rowGap: "4px",
      padding: "18px 0",
    },
  },
  kicker: {
    margin: 0,
    fontSize: "15px",
    fontWeight: 600,
    lineHeight: 1.7,
    color: SITE_COLOR.blue,
  },
  title: {
    margin: 0,
    fontSize: siteFluidFont(18, 1.5, 21),
    lineHeight: 1.4,
    fontWeight: 700,
    letterSpacing: "-.02em",
    color: SITE_COLOR.ink,
    textWrap: "balance",
  },
  text: {
    margin: "6px 0 0",
    fontSize: "15px",
    lineHeight: 1.7,
    color: SITE_COLOR.ink2,
    maxWidth: "32em",
  },
} as const;

interface EducationItemText {
  kicker: string;
  title: string;
  text: string;
}

/** 교육·컨설팅·법규·안전 교육 네 항목(홈·드론 측량 공통) — 대표 사진 한 장 옆에 목록으로. */
export function EducationSection({
  landing = false,
  sectionSx,
}: {
  landing?: boolean;
  sectionSx?: SxProps<Theme>;
}) {
  const { t } = useTranslation("landing");
  const titleId = useId();
  const cards = t("site.education.cards", {
    returnObjects: true,
  }) as readonly EducationItemText[];
  return (
    <SiteSection
      labelledBy={titleId}
      skipOffscreen={EDUCATION_OFFSCREEN_SIZE}
      sx={sectionSx}
    >
      <Box sx={SITE_WRAP}>
        <SectionHead
          titleId={titleId}
          eyebrow={t("site.education.eyebrow")}
          title={t("site.education.title")}
          landing={landing}
        />
        <Box sx={EDUCATION_STYLES.layout}>
          <Box
            component="figure"
            ref={revealRef}
            data-reveal="up"
            sx={EDUCATION_STYLES.photo}
          >
            <SiteImage
              name="consulting_plan"
              sizes="(max-width: 900px) 100vw, 640px"
              alt={t("site.education.photoAlt")}
            />
          </Box>
          <Box component="ul" sx={EDUCATION_STYLES.list}>
            {cards.map((card, index) => (
              <Box
                component="li"
                key={card.kicker}
                ref={revealRef}
                data-reveal="up"
                style={{ "--i": index + 1 } as CSSProperties}
                sx={EDUCATION_STYLES.item}
              >
                <Box component="p" sx={EDUCATION_STYLES.kicker}>
                  {card.kicker}
                </Box>
                <div>
                  <Box component="h3" sx={EDUCATION_STYLES.title}>
                    <SiteTitle text={card.title} />
                  </Box>
                  <Box component="p" sx={EDUCATION_STYLES.text}>
                    {card.text}
                  </Box>
                </div>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </SiteSection>
  );
}
